<script lang="ts">
  /**
   * Le Compagnon du Rôdeur.
   *
   * Son profil vient du bestiaire — un Loup des bois apprivoisé reste le Loup
   * des bois qu'on peut affronter, et il n'y a aucune raison d'en tenir deux
   * comptes. Ce qui lui appartient en propre est sur la fiche : son nom, ses
   * blessures, le Niveau d'Affinité atteint, et ce qu'on lui a appris.
   *
   * Les ordres et les Réflexes sont des textes libres : le livre en donne en
   * exemple et ne ferme pas la liste.
   */
  import { vieSauvage as lireLaVieSauvage, type PersonnageEnregistre } from '../../../rules'

  let {
    personnage,
    apprivoiser,
    instruire,
  }: {
    personnage: PersonnageEnregistre
    /** Apprivoise un animal, ou renvoie celui qu'on avait. */
    apprivoiser: (profil: string | null) => void
    /** Ce que le Compagnon subit, apprend, ou devient. */
    instruire: (
      quoi: 'nom' | 'pv' | 'affinite' | 'ordre' | 'reflexe',
      texte: string,
      valeur: number,
    ) => void
  } = $props()

  const sauvage = $derived(lireLaVieSauvage(personnage))
  let clairiere = $state(false)
  /** L'ordre ou le Réflexe qu'on est en train d'écrire. */
  let ordre = $state('')
  let reflexe = $state('')

  function apprendre(quoi: 'ordre' | 'reflexe') {
    const texte = quoi === 'ordre' ? ordre : reflexe
    if (texte.trim().length === 0) return
    instruire(quoi, texte, 0)
    if (quoi === 'ordre') ordre = ''
    else reflexe = ''
  }
</script>

<div class="cartouche">
  <div class="bandeau">
    Compagnon
    {#if clairiere}
      <button type="button" class="refermer" onclick={() => (clairiere = false)}>Refermer</button>
    {/if}
  </div>
  <div class="corps">
    {#if sauvage.compagnon}
      {@const c = sauvage.compagnon}
      <section class="bete">
        <h3>
          <input
            class="saisie nom-bete"
            type="text"
            value={c.nom}
            aria-label="Nom du Compagnon"
            onchange={(e) => instruire('nom', e.currentTarget.value, 0)}
          />
          <span class="voie"
            >{c.categorie ?? 'sans Catégorie'}{#if c.page}· p. {c.page}{:else}· écrit à la
              table{/if}</span
          >
          <button type="button" class="lien" onclick={() => apprivoiser(null)}>Renvoyer</button>
        </h3>

        <div class="etat">
          <span class="chiffre" title="Points de vie">
            <span class="etiquette">PV</span>
            <span class="valeur data" class:trop={c.pv === 0}>{c.pv}/{c.pv_max}</span>
          </span>
          {#if c.vitesse !== null}
            <span class="chiffre" title="Vitesse">
              <span class="etiquette">VIT</span>
              <span class="valeur data">{c.vitesse}</span>
            </span>
          {/if}
          {#if c.degats !== null}
            <span class="chiffre" title="Dégâts de son attaque naturelle">
              <span class="etiquette">DÉG</span>
              <span class="valeur data">{c.degats}</span>
            </span>
          {/if}
          <button type="button" class="pilule" onclick={() => instruire('pv', '', 1)}>−1 PV</button>
          <button type="button" class="pilule" onclick={() => instruire('pv', '', -1)}>+1 PV</button>
        </div>

        <div class="affinite">
          <h4>
            Affinité {c.affinite} — {c.affinite_nom}
            <button
              type="button"
              class="pilule"
              disabled={c.affinite >= 5}
              onclick={() => instruire('affinite', '', 1)}
            >
              Monter
            </button>
            <button
              type="button"
              class="pilule discret"
              disabled={c.affinite <= 1}
              onclick={() => instruire('affinite', '', -1)}
            >
              Descendre
            </button>
          </h4>
          <p class="detail">{c.affinite_resume}</p>
          {#each c.affinite_effets as e (e)}
            <span class="detail acquis">{e}</span>
          {/each}
        </div>

        <div class="lecons">
          <h4>Ordres</h4>
          <ul class="plates">
            {#each c.ordres as o (o)}
              <li>
                <span class="quoi">{o}</span>
                <button
                  type="button"
                  class="pilule discret"
                  onclick={() => instruire('ordre', o, 0)}
                >
                  Oublier
                </button>
              </li>
            {/each}
          </ul>
          <div class="rangee">
            <input
              class="saisie"
              type="text"
              placeholder={sauvage.ordres_types[0] ?? 'Attaque'}
              bind:value={ordre}
              list="ordres-types"
            />
            <datalist id="ordres-types">
              {#each sauvage.ordres_types as o (o)}
                <option value={o}></option>
              {/each}
            </datalist>
            <button type="button" class="pilule" onclick={() => apprendre('ordre')}>
              Lui apprendre
            </button>
          </div>

          <h4>Réflexes</h4>
          <ul class="plates">
            {#each c.reflexes as o (o)}
              <li>
                <span class="quoi">{o}</span>
                <button
                  type="button"
                  class="pilule discret"
                  onclick={() => instruire('reflexe', o, 0)}
                >
                  Oublier
                </button>
              </li>
            {/each}
          </ul>
          <div class="rangee">
            <input
              class="saisie"
              type="text"
              placeholder={sauvage.reflexes_types[0] ?? 'Défendre son maître'}
              bind:value={reflexe}
              list="reflexes-types"
            />
            <datalist id="reflexes-types">
              {#each sauvage.reflexes_types as o (o)}
                <option value={o}></option>
              {/each}
            </datalist>
            <button type="button" class="pilule" onclick={() => apprendre('reflexe')}>
              Lui apprendre
            </button>
          </div>
          <p class="regle">
            Le livre en donne quelques-uns en exemple et ne ferme pas la liste :
            ce que vous écrivez ici est ce dont la table est convenue.
          </p>
        </div>
      </section>
    {:else if !clairiere}
      <p class="chapo">
        Aucun Compagnon. Apprivoiser demande un Test de Dressage cumulatif : la
        Catégorie de l'animal dit ce qu'il coûte et combien de Degrés il faut
        réunir.
      </p>
      <button type="button" class="lien" onclick={() => (clairiere = true)}>
        Apprivoiser un animal
      </button>
    {/if}

    {#if clairiere}
      <p class="regle">
        Le Test se jette à la table, sur plusieurs tentatives : l'application
        enregistre le résultat, elle ne le remplace pas.
      </p>
      <ul class="achats larges">
        {#each sauvage.animaux as a (a.id)}
          <li>
            <span class="nom">
              {a.nom}
              <span class="exigence">
                {a.categorie ?? '—'} · {a.objectif_dr} Degrés à réunir
                {#if a.modificateur !== 0}· {a.modificateur} au Test{/if}
                {#if a.pv !== null}· {a.pv} PV{/if}
                {#if a.page}· p. {a.page}{/if}
              </span>
            </span>
            <span class="chiffres data"></span>
            <button
              type="button"
              class="pilule"
              onclick={() => {
                apprivoiser(a.id)
                clairiere = false
              }}
            >
              Apprivoiser
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
</div>

<style>
  /* La bête : son nom se saisit, son état se lit d'un coup d'œil. */
  .bete {
    display: grid;
    gap: var(--pas);
  }
  .bete h3 {
    margin: 0;
    display: flex;
    align-items: baseline;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  .nom-bete {
    font-family: var(--titre);
    font-size: 0.95rem;
    max-width: 24ch;
  }
  .etat {
    display: flex;
    align-items: center;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  .etat .chiffre {
    display: grid;
    justify-items: center;
    gap: calc(var(--pas) * 0.5);
    background: var(--surface-2);
    border: 1px solid var(--line);
    border-radius: var(--r);
    padding: calc(var(--pas) * 1.5);
  }
  .etiquette {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-soft);
  }
  .etat .valeur.trop {
    color: var(--ko);
  }
  /* L'Affinité et les leçons : deux blocs en retrait, comme deux chapitres. */
  .affinite,
  .lecons {
    border-left: 2px solid var(--trait);
    padding-left: var(--pas);
    display: grid;
    gap: 2px;
  }
  h4 {
    margin: var(--pas) 0 0;
    font-family: var(--titre);
    font-size: 0.85rem;
    display: flex;
    align-items: baseline;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  .lecons h4:first-child {
    margin-top: 0;
  }
  /* Ce que l'Affinité a déjà donné : les Niveaux se cumulent. */
  .acquis {
    color: var(--ok);
  }
  .rangee {
    display: flex;
    align-items: center;
    gap: calc(var(--pas) / 2);
    flex-wrap: wrap;
    margin-bottom: var(--pas);
  }
  .rangee .saisie {
    max-width: 28ch;
  }
  .cartouche {
    margin-bottom: calc(var(--pas) * 3);
  }
  .cartouche .corps {
    padding: calc(var(--pas) * 3);
    gap: calc(var(--pas) * 3);
  }
</style>
